import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, DatePicker } from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';

import { PatientListModesMap } from '../../../@types/appointments';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import {
  modeAtom,
  operatoryFilterAtom,
  searchAtom,
  selectDateAtom,
  selectedProvidersAtom,
  viewRecentPatientsAtom,
} from '../../../modules/patientList/atoms';
import { ActionableViewButtons } from '../../../modules/patientList/patientStrip/actionableViewButtons';
import { PrivacyButton } from '../../../modules/patientList/PrivacyButton';
import {
  type PatientListPageLayoutContext,
  PatientListPageLayoutContextMap,
} from '../../../modules/patientList/types';
import { UploadRadiographs } from '../../../modules/patientList/uploadRadiographs';
import type { Appointment } from '../../../react-query/useAppointments';
import type { GetTaskResponse } from '../../../react-query/useGetTasks';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { useDevtoolsShortcut } from '../../../utils/hooks/useDevtoolsShortcut';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

import { AmbientTopbarContent } from './contextSpecificContent/AmbientTopbarContent';
import { AutoVerifyTopbarContent } from './contextSpecificContent/AutoVerifyTopbarContent';
import { ClaimsTopbarContent } from './contextSpecificContent/ClaimsTopbarContent';
import { ReferralsTopbarContent } from './contextSpecificContent/ReferralsTopbarContent';
import { Legend } from './Legend';
import { PatientListActions } from './PatientListActions';
import { PatientSearch } from './PatientSearch';
import { PrintButton } from './PrintButton';

export type ActionsTopbarProps = {
  appointmentIdsToTasks?: Record<string, GetTaskResponse>;
  appointments: Appointment[];
  context: PatientListPageLayoutContext;
  nextAppointmentIndex?: number;
  visitSummaries?: TVisitSummary[];
  isNextAppointmentInView?: boolean;
  isRefreshRequested?: boolean;
  hasNewAnalyses?: boolean;
  isLoading: boolean;
  onRefresh?: () => void;
  onShowNewAnalyses?: () => void;
  onScrollNextAppointmentIntoView?: () => void;
  onCreateReferral?: () => void;
};

export const ActionsTopbar = ({
  appointmentIdsToTasks,
  context,
  visitSummaries,
  hasNewAnalyses,
  isLoading,
  appointments,
  // Huddle specific props
  nextAppointmentIndex = 0,
  isNextAppointmentInView = false,
  isRefreshRequested = false,
  onRefresh = () => {},
  onShowNewAnalyses = () => {},
  onScrollNextAppointmentIntoView = () => {},
  onCreateReferral,
}: ActionsTopbarProps) => {
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);

  const [date, setDate] = useAtom(selectDateAtom);

  const setViewRecentPatients = useSetAtom(viewRecentPatientsAtom);
  const setSelectedOperatories = useSetAtom(operatoryFilterAtom);
  const setSelectedProviders = useSetAtom(selectedProvidersAtom);

  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const { showDevtools } = useDevtoolsShortcut();
  const dateLocale = useGetDateFNSLocale();
  const hideHuddle = useShouldHideHuddle();

  const isHuddle = context === PatientListPageLayoutContextMap.Huddle;

  const handleSelectDate = (
    newDate: Date,
    source: 'PREV' | 'NEXT' | 'TODAY' | 'CALENDAR',
  ) => {
    trackEvent(mixpanelEvents.huddleSelectDate, {
      date: newDate,
      isTodayButton: source === 'TODAY',
      source,
      context,
    });
    setDate(newDate);
    setSelectedOperatories([]);
    setSelectedProviders([]);
  };

  const renderContextContent = () => {
    switch (context) {
      case PatientListPageLayoutContextMap.Claims:
        return <ClaimsTopbarContent />;
      case PatientListPageLayoutContextMap.Referrals:
        return <ReferralsTopbarContent />;
      case PatientListPageLayoutContextMap.AutoVerify:
        return <AutoVerifyTopbarContent />;
      case PatientListPageLayoutContextMap.Ambient:
        return <AmbientTopbarContent />;
      case PatientListPageLayoutContextMap.Huddle:
      default:
        return (
          <div className="vai-min-w-32 vai-max-w-64">
            <PatientSearch context={PatientListPageLayoutContextMap.Huddle} />
          </div>
        );
    }
  };

  return (
    <div className="vai-bg-white vai-py-2 vai-px-4 vai-shadow-sm vai-flex vai-items-center vai-gap-4">
      {/* Date Picker - Only for Huddle */}
      {isHuddle && (
        <DatePicker
          date={date || undefined}
          setDate={handleSelectDate}
          buttonClassName={classNames({
            'vai-text-destructive vai-border-destructive':
              !date && !(search.length > 2),
          })}
          placeholderText={t`Pick a date`}
          locale={dateLocale}
          showPreviousNextButtons
          showTodayButton
          todayButtonText={t`Back to Today`}
        />
      )}

      {/* Devtools button - Only for Huddle */}
      {mode === PatientListModesMap.DailyHuddle && showDevtools && (
        <Button
          onClick={() => {
            setViewRecentPatients();
            trackEvent(mixpanelEvents.huddleRecentVisitsClick);
          }}
        >
          Recent Visits
        </Button>
      )}

      {/* Context-specific content */}
      {renderContextContent()}

      {/* Common elements */}
      <div className="vai-flex vai-items-center vai-gap-2 vai-ml-auto">
        {/* Huddle-specific elements - positioned on the right */}
        {isHuddle && !hideHuddle && (
          <>
            <ActionableViewButtons loading={isLoading} />
            <Legend />
          </>
        )}
        {isHuddle && <PrivacyButton source="PATIENT_LIST" />}
        {isHuddle && !hideHuddle && (
          <>
            <PrintButton
              appointments={appointments}
              appointmentIdsToTasks={appointmentIdsToTasks || {}}
              visitSummaries={visitSummaries}
            />
            <PatientListActions
              hasNewAnalyses={!!hasNewAnalyses}
              nextAppointmentIndex={nextAppointmentIndex}
              isNextAppointmentInView={isNextAppointmentInView}
              isRefreshRequested={isRefreshRequested}
              onRefresh={onRefresh}
              onScrollNextAppointmentIntoView={onScrollNextAppointmentIntoView}
              onShowNewAnalyses={onShowNewAnalyses}
            />
          </>
        )}
        {/* Create Referral Button - positioned before privacy button */}
        {context === PatientListPageLayoutContextMap.Referrals &&
          onCreateReferral && (
            <Button
              onClick={onCreateReferral}
              className="vai-flex vai-items-center vai-gap-2"
              size="sm"
            >
              <FontAwesomeIcon icon={faPlus} className="vai-h-4 vai-w-4" />
              <Trans>Referral</Trans>
            </Button>
          )}
        {/* Privacy button for non-huddle contexts */}
        {!isHuddle && (
          <PrivacyButton
            source={
              context === PatientListPageLayoutContextMap.Claims
                ? 'CLAIMS'
                : context === PatientListPageLayoutContextMap.AutoVerify
                ? 'AUTOVERIFY'
                : context === PatientListPageLayoutContextMap.Referrals
                ? 'REFERRALS'
                : 'PATIENT_LIST'
            }
          />
        )}
        {/* Search Bar for Claims and Auto Verify - positioned after privacy button */}
        {(context === PatientListPageLayoutContextMap.Claims ||
          context === PatientListPageLayoutContextMap.AutoVerify) && (
          <div className="vai-w-80">
            <PatientSearch context={context} />
          </div>
        )}
        {showDevtools && <UploadRadiographs />}
      </div>
    </div>
  );
};
